import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";

import { Toast } from "primereact/toast";
import { Nullable } from "primereact/ts-helpers";
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Tooltip } from "primereact/tooltip";
import { confirmDialog } from "primereact/confirmdialog";
import { Image } from "primereact/image";

import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import ErrorPage from "../../../../../Components/ErrorPage";
import DatePicker from "../../../../../Components/DatePicker";
import Textarea from "../../../../../Components/TextArea";
import PageLoader from "../../../../../Components/PageLoader";
import { goToTop } from "../../../../../Components/goToTop";
import { formatDate, parseStringToDate } from "../../../../../Utils/getDate";
import { checkFileOtherList, checkText } from "../../../../../Utils/Validator";
import { documentFileExtension, imageFileExtension } from "../../../../../Api/FileConstant";
import { getLoginUserRoleName, UserRoleName } from "../../../../../Utils/LoginUserRoleName";

import {
    CommencementNotification as CommencementNotificationType,
} from "../../../../../Controllers/Types/CommencementNotification/CommencementNotificationTypes";


import { useSelector } from "react-redux";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { RootState } from "../../../../../Controllers/Store";
import { getCommencementNotificationByFolderId } from "../../../../../Controllers/Thunks/BuildingInspectorThunks";
import { addAndUpdateCommencementNotification, deleteCommencemntNotificationFile } from "../../../../../Api/BuildingInspectorService/BuildingInspectorService";
import FileActionLoader from "../../../../../Components/FileActionLoader";
import CustomTableLoader from "../../../../../Components/CustomTableLoader";

const CommencementNotification: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);

    const projectData = location.state?.data;
    const folderId = Number(projectData.folder_id);

    const [commencementData, setCommencementData] = useState<CommencementNotificationType | null>(null);

    const [showCommencementModal, setShowCommencementModal] = useState<boolean>(false);

    const [date, setDate] = useState<Nullable<Date>>(null);
    const [description, setDescription] = useState<string>('');
    const [files, setFiles] = useState<File[]>([]);

    const [dateError, setDateError] = useState<string>('');
    const [descriptionError, setDescriptionError] = useState<string>('');
    const [filesError, setfilesError] = useState<string>('');

    const [commencementUpdateHasChanges, setCommencementUpdateHasChanges] = useState<boolean>(false);
    const [documentFilePath, setDocumentFilePath] = useState<string>("");
    const [showDocumentViewModal, setShowDocumentViewModal] = useState<boolean>(false);
    const [fileDeleteLoadingId, setFileDeleteLoadingId] = useState<number | null>(null);

    const [isViewOnly, setIsViewOnly] = useState<boolean>(false);

    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };

        fetchUserRole();
    }, []);


    // PERMISION HANDLER
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const sent_notification = useCanEdit("Commencement Notification", "sent_notification");
    const view_notification = useCanEdit("Commencement Notification", "view_notification");

    useEffect(() => {
        setIsViewOnly((view_notification === true && sent_notification === false) ? true : false);
    }, [view_notification, sent_notification]);

    // for fetching data fro api tigger with state object actions
    useEffect(() => {
        commencementNotificationDataGet();
    }, [folderId]);

    const commencementNotificationDataGet = async () => {
        await dispatch(getCommencementNotificationByFolderId(folderId));
        setInitialDataLoading(false);
    }

    // this is get data from state list and set projectlist
    const commencementNotification = useAppSelector((state) => state.commencementNotificationDetails.commencementNotification);
    const loadingCommencementNotification = useAppSelector((state) => state.commencementNotificationDetails.loader);
    const errorCommencementNotification = useAppSelector((state) => state.commencementNotificationDetails.errorDisplay);

    useEffect(() => {
        if (commencementNotification && commencementNotification != null) {
            setCommencementData(commencementNotification);
        } else {
            setCommencementData(null);
        }
    }, [commencementNotification]);


    /* *************************** For commencement ****************************** */
    useEffect(() => {
        if (commencementData && commencementData !== null) {
            if (formatDate(date!) !== commencementData?.date) {
                setCommencementUpdateHasChanges(true);
            } else if (description !== commencementData?.description) {
                setCommencementUpdateHasChanges(true);
            } else if (files && files?.length !== 0) {
                setCommencementUpdateHasChanges(true);
            } else {
                setCommencementUpdateHasChanges(false);
            }
        }
    }, [commencementData, date, description, files]);


    const handleCloseCommencementModal = () => {
        setShowCommencementModal(false);

        handleResetCommencementFields();
        handleResetCommencementErrors();
    }

    const handleResetCommencementFields = () => {
        setDate(null);
        setDescription('');
        setFiles([]);
    }

    const handleResetCommencementErrors = () => {
        setDateError('');
        setDescriptionError('');
        setfilesError('');
    }

    const handleAddCommencement = () => {
        setDataState('Add');
        setShowCommencementModal(true);
    }

    const handleEditCommencement = () => {
        if (commencementData && commencementData !== null) {
            setDataState('Edit');
            setShowCommencementModal(true);

            setDate(parseStringToDate(commencementData?.date!));
            setDescription(commencementData?.description!);
        }
    }

    function checkCredentialForCommencement(): boolean {
        const isValidDate = checkText(formatDate(date!), true, setDateError);
        const isValidDescription = checkText(description!, true, setDescriptionError);
        const isValidUploadedFiles = checkFileOtherList(files, ((dataState === 'Add' || (dataState === 'Edit' && commencementData?.notification_document?.length === 0)) ? true : false), setfilesError);

        return isValidDate && isValidDescription && isValidUploadedFiles;
    }

    function showAPIErrorCommencement(keyName: string, error: string): void {
        if (keyName === "date") {
            setDateError(error);
        }

        if (keyName === 'description') {
            setDescriptionError(error);
        }

        if (keyName === 'file_list[]') {
            setfilesError(error);
        }
    }

    /* Clear validation message */
    useEffect(() => {
        checkText(formatDate(date!), false, setDateError);
    }, [date]);

    useEffect(() => {
        checkText(description!, false, setDescriptionError);
    }, [description]);

    useEffect(() => {
        checkFileOtherList(files, false, setfilesError);
    }, [files]);


    // this is database function to call api service.
    const handleSaveCommencement = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentialForCommencement()) {
            setLoading(true);

            const data = await addAndUpdateCommencementNotification(
                folderId,
                formatDate(date!),
                description,
                files
            );

            setLoading(false);

            if (data.statusCode === 200) {
                commencementNotificationDataGet();
                handleCloseCommencementModal();

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Commencement details ${dataState === 'Add' ? 'added' : 'updated'} successfully.`,
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorCommencement(key, value[0])
                );
            }
            else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: `There was an error ${dataState === 'Add' ? 'adding' : 'updating'} the commencement details.`,
                        life: 3000,
                    });
                }
            }
        }
    }

    /* Modal header */
    const commencementModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Commencement Notification Details
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseCommencementModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    /* Modal footer */
    const commencementModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseCommencementModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Submit' : 'Update'}
                onClick={handleSaveCommencement}
                loading={loading} className="custom_btn primary"
                disabled={dataState === 'Edit' && !commencementUpdateHasChanges}
            />
        </div>
    );
    /* *************************************************************************** */


    const handleCloseFileViewModal = () => {
        setShowDocumentViewModal(false);
    };

    const handleViewDocument = (filePath: string) => {
        if (filePath && filePath !== "") {
            setShowDocumentViewModal(true);
            setDocumentFilePath(filePath);
        } else {
            setDocumentFilePath("");
        }
    };

    const handleDeleteFile = (fileId: number) => {
        confirmDialog({
            message: "Are you sure you want to delete this file?",
            header: "Confirm the deletion",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => deleteFile(fileId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    };

    const deleteFile = async (fileId: number) => {
        setFileDeleteLoadingId(fileId);
        const data = await deleteCommencemntNotificationFile(fileId);

        setFileDeleteLoadingId(null);
        if (data.statusCode === 200) {
            commencementNotificationDataGet();

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "File deleted successfully.",
                    life: 2000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error deleting the file.",
                    life: 3000,
                });
            }
        }
    }

    const handleUploadFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const filesArray = Array.from(event.target.files);
            setFiles((prev) => [...prev, ...filesArray]);
        }
    };

    const handleRemoveFile = (fileIndex: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== fileIndex));
    };


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingCommencementNotification && initialDataLoading ? (
                    <PageLoader />
                ) : errorCommencementNotification ? (
                    <ErrorPage errorMessage={errorCommencementNotification} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-sm-3 mt-2">
                            <div className="page_action_area">
                                <button
                                    className={`add_data_link_btn ${commencementData && commencementData !== null ? 'fill' : ''} p-ripple`}
                                    onClick={commencementData && commencementData !== null ? handleEditCommencement : handleAddCommencement}
                                >
                                    <i className="bi bi-bell"></i>
                                    <span>Commencement</span>
                                    <Ripple />
                                </button>
                            </div>
                        </div>

                        <div className="page_main_content_section">
                            {loadingCommencementNotification && (
                                <CustomTableLoader />
                            )}

                            <div className="commencement_area">
                                <p className="text_semi_bold">
                                    The definition of commencement is different for different building types
                                </p>

                                <hr />

                                <p>
                                    Complex buildings are those on a shared foundation plinth or podium of another building, a building with two or more basement levels or a public building with capacity for 100 or more visitors. For these buildings, commencement is when the foundations and structure of the lowest floor level are completed.
                                </p>

                                <p>
                                    For non-complex buildings, if those works are for a new building or an extension which enlarges the footprint of an existing building then commencement is when the foundations and ground floor structure are complete.
                                </p>

                                <p>
                                    For all other works, such as loft conversions and internal alterations, commencement is when 15% of the works are completed. This will have been defined in the initial notice.
                                </p>

                                <p>
                                    If the works have reached commencement then please notify us by clicking below.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>


            {/* Commencement modal */}
            <Dialog
                visible={showCommencementModal}
                headerClassName="custom_modal_header"
                header={commencementModalHeader}
                footer={!isViewOnly && commencementModalFooter}
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseCommencementModal}
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        <div className="row">
                            {/* Date */}
                            <div className="col-12">
                                <DatePicker
                                    key={`date`}
                                    id="date"
                                    label="Date"
                                    labelHtmlFor="date"
                                    required={!isViewOnly && true}
                                    value={date}
                                    placeholder="dd/mm/yyyy"
                                    onChange={setDate}
                                    error={dateError}
                                    showClear={!isViewOnly}
                                    onClear={() => setDate(null)}
                                    inputDisabled={isViewOnly}
                                    inputClassName={`${isViewOnly ? 'view_only_input' : ''}`}
                                />
                            </div>

                            {/* Description */}
                            <div className="col-12">
                                <Textarea
                                    id="description"
                                    label="Description"
                                    labelHtmlFor="description"
                                    required={!isViewOnly && true}
                                    value={description}
                                    placeholder="Description"
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                                    error={descriptionError}
                                    inputDisabled={isViewOnly}
                                    textAreaClassName={`${isViewOnly ? 'view_only_input' : ''}`}
                                />
                            </div>

                            {/* Notification documents */}
                            <div className="col-12">
                                <div className="page_form_group mb-1">
                                    <label htmlFor="documents" className="custom_form_label is_required">Files</label>

                                    <div className="checklist_img_container mt-2 mb-1">
                                        {dataState === 'Edit' && (
                                            <React.Fragment>
                                                {commencementData?.notification_document && commencementData?.notification_document?.length > 0 && (
                                                    <React.Fragment>
                                                        {commencementData?.notification_document?.map((file, index) => (
                                                            <div className="note_image_container response_image_container" key={file?.id}>
                                                                {fileDeleteLoadingId === file?.id ? (
                                                                    <FileActionLoader />
                                                                ) : (
                                                                    <React.Fragment>
                                                                        {file?.extension === "pdf" ? (
                                                                            <div
                                                                                className="response_doc_file_container"
                                                                                onClick={() => handleViewDocument(file?.path!)}
                                                                            >
                                                                                <i className="bi bi-file-earmark-pdf"></i>
                                                                            </div>
                                                                        ) : (
                                                                            <Image
                                                                                src={file?.path!}
                                                                                alt={`${index + 1}`}
                                                                                className="note_img response_image"
                                                                                preview
                                                                                downloadable
                                                                            />
                                                                        )}
                                                                    </React.Fragment>
                                                                )}

                                                                {!isViewOnly && (
                                                                    <button
                                                                        className="img_clear_btn p-ripple"
                                                                        onClick={() => handleDeleteFile(file?.id!)}
                                                                    >
                                                                        <i className="bi bi-x"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </React.Fragment>
                                                )}
                                            </React.Fragment>
                                        )}

                                        {files && files?.length > 0 && (
                                            <React.Fragment>
                                                {files.map((file, index) => {
                                                    const fileExtension = file.name?.split(".").pop()?.toLowerCase();

                                                    return (
                                                        <div
                                                            className="note_image_container response_image_container"
                                                            key={index}
                                                        >
                                                            {fileExtension === "pdf" ? (
                                                                <div
                                                                    className="response_doc_file_container"
                                                                    onClick={() => handleViewDocument(URL.createObjectURL(file))}
                                                                >
                                                                    <i className="bi bi-file-earmark-pdf"></i>
                                                                </div>
                                                            ) : (
                                                                <Image
                                                                    src={URL.createObjectURL(file)}
                                                                    alt={`${index + 1}`}
                                                                    className="note_img response_image"
                                                                    preview
                                                                />
                                                            )}

                                                            <button
                                                                className="img_clear_btn p-ripple"
                                                                onClick={() => handleRemoveFile(index)}
                                                            >
                                                                <i className="bi bi-x"></i>
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </React.Fragment>
                                        )}

                                        <Tooltip target={`.img_upload_label`} />

                                        {!isViewOnly && (
                                            <label
                                                htmlFor="arcResponseFiles"
                                                className="img_upload_label p-ripple"
                                                data-pr-tooltip="Upload files"
                                                data-pr-position="right"
                                            >
                                                <i className="bi bi-upload"></i>
                                                <input
                                                    id="arcResponseFiles"
                                                    multiple
                                                    type="file"
                                                    accept={documentFileExtension + ', ' + imageFileExtension}
                                                    onChange={handleUploadFiles}
                                                />
                                                <Ripple />
                                            </label>
                                        )}
                                    </div>

                                    {!isViewOnly && (
                                        <span className="file_upload_info pt-1">
                                            <i className="bi bi-info-circle"></i> Acceptable file format : {documentFileExtension + ', ' + imageFileExtension}
                                        </span>
                                    )}

                                    {filesError &&
                                        <small className="form_error_msg">{filesError}</small>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showDocumentViewModal}
                onClose={handleCloseFileViewModal}
                fileUrl={documentFilePath}
                canDownload={documentFilePath ? true : false}
            />
            {/*  */}
        </React.Fragment>
    )
}

export default CommencementNotification;